import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * DRAGGING A PLAYER INTO A CHAIR.
 *
 * The owner asked for drag-and-drop on the seating board and got tap-to-select, which was my call
 * to raise and not my call to make. This is the drag, added ALONGSIDE the tap rather than replacing
 * it — see the note on touch below, and `SeatingBoard`'s header comment.
 *
 * **Pointer Events, not the HTML5 drag-and-drop API.** HTML5 DnD cannot be styled (the drag image
 * is a browser screenshot), fires no useful events on the drop target until the end, and does not
 * exist on touch at all. Pointer events are one code path for mouse, pen and touch, and we decide
 * what a drag looks like.
 *
 * **Touch deliberately does not drag.** Starting a drag from a finger means claiming the gesture
 * with `touch-action: none`, and a chair that eats vertical swipes is a board of seven tables you
 * cannot scroll past. So on touch the press falls through to the tap path, which was built for
 * phones and is unchanged. Directors on a phone lose nothing; directors on a laptop get the drag
 * they asked for.
 *
 * **A press is not yet a drag.** Nothing happens until the pointer has moved `THRESHOLD` pixels, so
 * an ordinary click still reads as a click — without it every tap becomes a one-pixel drag and the
 * tap path dies quietly.
 */

/** Pixels of movement before a press becomes a drag. Below this it is a click. */
const THRESHOLD = 5;

export interface Dragging {
  enrollmentId: string;
  /** What the ghost under the cursor says. */
  label: string;
  x: number;
  y: number;
  /** The `data-chair` of the chair under the pointer, or null over anything else. */
  over: string | null;
}

export interface SeatDrag {
  dragging: Dragging | null;
  /** Attach to `onPointerDown` on anything holding a player. */
  start: (enrollmentId: string, label: string, event: React.PointerEvent) => void;
  /**
   * Call FIRST in an `onClick` that also drags. Returns true when the click is the tail of a drag
   * that just ended and should be ignored.
   */
  swallowClick: () => boolean;
}

export function useSeatDrag(onDrop: (chairKey: string, enrollmentId: string) => void): SeatDrag {
  const [dragging, setDragging] = useState<Dragging | null>(null);

  /** Pressed, but not yet moved far enough to count. A pointerup from here is a plain click. */
  const pending = useRef<{ enrollmentId: string; label: string; x: number; y: number } | null>(null);
  /** The live drag. Kept in a ref as well as state because the window listeners close over it. */
  const active = useRef<Dragging | null>(null);

  /**
   * A finished drag still produces a `click` from the browser. Left alone it would reach the tap
   * handler and pick the player straight back up, so it is swallowed once — and cleared on the next
   * press rather than on a timer, so a drag that somehow produces no click cannot eat a later one.
   */
  const suppressClick = useRef(false);

  /** So the window listeners, which are attached once, always call the current handler. */
  const drop = useRef(onDrop);
  drop.current = onDrop;

  const start = useCallback((enrollmentId: string, label: string, event: React.PointerEvent) => {
    suppressClick.current = false;

    /** Touch scrolls the page. Pen and mouse drag. */
    if (event.pointerType === 'touch') return;
    /** Left button only — a right-click is a context menu, not a move. */
    if (event.button !== 0) return;

    pending.current = { enrollmentId, label, x: event.clientX, y: event.clientY };
  }, []);

  const swallowClick = useCallback(() => {
    if (!suppressClick.current) return false;
    suppressClick.current = false;
    return true;
  }, []);

  useEffect(() => {
    /**
     * What is under the pointer, asked of the document rather than tracked from element rects.
     * Rects go stale the moment the board reflows — which it does on every move, since placing
     * someone re-sorts the chairs.
     */
    function chairUnder(x: number, y: number): string | null {
      const element = document.elementFromPoint(x, y);
      return element?.closest<HTMLElement>('[data-chair]')?.dataset.chair ?? null;
    }

    function onMove(event: PointerEvent) {
      const held = pending.current;
      if (!held) return;

      if (!active.current) {
        const travelled = Math.hypot(event.clientX - held.x, event.clientY - held.y);
        if (travelled < THRESHOLD) return;
      }

      const next: Dragging = {
        enrollmentId: held.enrollmentId,
        label: held.label,
        x: event.clientX,
        y: event.clientY,
        over: chairUnder(event.clientX, event.clientY),
      };

      active.current = next;
      setDragging(next);
    }

    function onUp() {
      const finished = active.current;
      pending.current = null;
      active.current = null;
      setDragging(null);

      if (!finished) return;

      suppressClick.current = true;
      /** Dropped on nothing: the move is abandoned, which is what letting go somewhere else means. */
      if (finished.over) drop.current(finished.over, finished.enrollmentId);
    }

    /** The browser took the gesture back — a scroll, an alt-tab, a touch turning into a pan. */
    function onCancel() {
      pending.current = null;
      active.current = null;
      setDragging(null);
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, []);

  /**
   * Dragging across text selects it, which leaves the board smeared blue and the next click
   * landing on a selection instead of a chair. `preventDefault` on pointermove does not stop it.
   */
  useEffect(() => {
    if (!dragging) return;

    const { body } = document;
    const previousSelect = body.style.userSelect;
    const previousCursor = body.style.cursor;

    body.style.userSelect = 'none';
    body.style.cursor = 'grabbing';

    return () => {
      body.style.userSelect = previousSelect;
      body.style.cursor = previousCursor;
    };
  }, [dragging]);

  return { dragging, start, swallowClick };
}

/** `"3:east"` — the table and the chair, as written into `data-chair`. */
export function chairKey(tableNumber: number, seat: string): string {
  return `${tableNumber}:${seat}`;
}

export function parseChairKey(key: string): { tableNumber: number; seat: string } | null {
  const [table, seat] = key.split(':');
  const tableNumber = Number(table);
  if (!seat || Number.isNaN(tableNumber)) return null;
  return { tableNumber, seat };
}
